import {
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import "../Login/Login.css";
import axios from "axios";
import emailIcon from "../../assets/login_mail.svg";
import passwordIcon from "../../assets/login_password.svg";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import HomeNavbar from "../../Components/HomeNavbar/HomeNavbar";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoginFormSchema, LoginFormSchemaType } from "./LoginSchema";
import TextField from "../../Components/TextField";
import { useRegion } from "../../context/RegionContext";
import { REGIONS } from "../../constants/regions";
import { images } from "../../assets/images";

const Login = () => {
  const navigate = useNavigate();
  const { changeRegion } = useRegion();
  // const { showSnackbar } = useSnackbar();

  const { register, handleSubmit, formState, watch } =
    useForm<LoginFormSchemaType>({
      mode: "onTouched",
      resolver: yupResolver(LoginFormSchema),
      defaultValues: { email: "", password: "" },
    });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string>("");
  const [Loading, setLoading] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  useEffect(() => {
    const storedEmail = localStorage.getItem("rememberedEmail");
    const storedPassword = localStorage.getItem("rememberedPassword");
    const storedRememberMe = localStorage.getItem("rememberMe");

    if (storedRememberMe === "true") {
      setEmail(storedEmail || "");
      setPassword(storedPassword || "");
      setRememberMe(true);
    }
  }, []);

  const emailValue = watch("email");
  const passwordValue = watch("password");

  const [passwordError, setPasswordError] = useState("");

  useEffect(() => setPasswordError(""), [passwordValue]);
  useEffect(() => setError(""), [emailValue]);

  const handleLogin = async (e: LoginFormSchemaType) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BASE_URL}/clients/user/login`,
        {
          email: e.email,
          password: e.password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data.statusCode === "OK") {
        localStorage.setItem("userId", response.data.data.id);

        // countryId lives only in /me (meta.countryId). Fetch it once here so the
        // X-Region header is set in localStorage from login and persists across
        // refreshes — otherwise page requests race Sidenav's later /me call.
        try {
          const meRes = await axios.get(
            `${import.meta.env.VITE_BASE_URL}/clients/user/me`,
            { headers: { Authorization: response.data.data.id } }
          );
          const meta = meRes.data?.data?.meta;
          const countryId = meta?.countryId;
          const rawRegion = (meta?.countryName ?? "").toLowerCase();
          const region =
            rawRegion === "uae" || rawRegion === "ae" ? REGIONS.AE
            : rawRegion === "ksa" || rawRegion === "sa" ? REGIONS.SA
            : null;
          if (meta?.companyId) localStorage.setItem("companyId", meta.companyId);
          if (region && countryId) {
            changeRegion(region, countryId);
          }
        } catch (err) {
          console.error("Error fetching region from /me at login:", err);
        }

        navigate("/dashboard");
        setError("");
      } else {
        if (response?.data?.responseInfo?.httpCode === 401) {
          setPasswordError(response?.data?.responseInfo?.description);
        } else {
          setError(response?.data?.responseInfo?.description);
        }
      }
    } catch (error) {
      console.error("Error during login:", error);
      // Backend errors (invalid credentials, missing country, etc.) come back as
      // non-2xx, so axios throws into here. Surface the right message.
      const info = axios.isAxiosError(error)
        ? error.response?.data?.responseInfo
        : undefined;
      if (info?.httpCode === 401 || info?.httpCode === 404) {
        // Keep credential failures generic — don't reveal which field is wrong.
        setError("Invalid email or password");
      } else if (info?.description) {
        setError(info.description);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }

    if (rememberMe) {
      localStorage.setItem("rememberedEmail", email);
      localStorage.setItem("rememberedPassword", password);
      localStorage.setItem("rememberMe", "true");
    } else {
      // Clear remembered data if Remember Me is unchecked
      localStorage.removeItem("rememberedEmail");
      localStorage.removeItem("rememberedPassword");
      localStorage.removeItem("rememberMe");
    }
  };

  return (
    <div className="login_navbar_container">
      <HomeNavbar isHome={true} />
      <div className="main_login_container_ak">
        <div className="login_content_wrapper">
          <div className="login_body_wrapper">
            <div className="login_content_holder">
              <h1 className="white_text">
                Post Jobs & Find the Right Talent Instantly
              </h1>
              <div className="white_text">
                Effortlessly post job openings, filter applicants with advanced
                AI tools, and connect directly with Emirati talent. Simplify
                your hiring process today!
              </div>
              <img
                src={images.macbookProLight}
                alt="dashboard"
                className="laptop_img"
              />
            </div>
          </div>
          <div className="login_form_wrapper_ak">
            <div className="login">
              <div className="login-form-container">
                <h2 className="text-center">Log in to your Account</h2>
                <p className="gray_text text-center">
                  Welcome back! Please enter your details
                </p>
                <form
                  className="login-form textFieldWrapper"
                  onSubmit={handleSubmit(handleLogin)}
                >
                  <div className="textFieldSpacing">
                    <TextField
                      label=""
                      fullWidth
                      placeholder="Email"
                      autoComplete="new-password"
                      {...register("email")}
                      error={!!formState.errors.email || !!error}
                      helperText={formState.errors.email?.message || error}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <img src={emailIcon} alt="email_icon" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label=""
                      fullWidth
                      placeholder="Password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      {...register("password")}
                      error={!!formState.errors.password || !!passwordError}
                      helperText={
                        formState.errors.password?.message || passwordError
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <img
                              onClick={handleTogglePasswordVisibility}
                              src={passwordIcon}
                              alt="password_icon"
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                            style={{ marginRight: "4px" }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        ),
                      }}
                    />
                  </div>
                  <br />

                  <div className="checkbox-container">
                    <div className="remember-container">
                      <Checkbox
                        id="remember"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        size="small"
                        color="success"
                      />
                      <label htmlFor="remember" className="remember-label">
                        Remember Me
                      </label>
                    </div>
                    <Link
                      to={`/forgetPassword`}
                      style={{ textDecoration: "none" }}
                    >
                      {" "}
                      <h1 className="forgot-label">Forgot password?</h1>
                    </Link>
                  </div>
                  <div className="submit-container">
                    {Loading ? (
                      <button className="submit-button">
                        <CircularProgress size={30} />
                      </button>
                    ) : (
                      <button type="submit" className="submit-button">
                        Sign in
                      </button>
                    )}
                  </div>
                </form>
                <div>
                  <p className="gray_text text-center">
                    Don’t have an account?
                  </p>
                  <Link to={`/signup`} style={{ textDecoration: "none" }}>
                    {" "}
                    <h1 className="forgot-label text-center">
                      Create an account
                    </h1>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
